import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  message,
  Spin,
} from "antd";
import { SendOutlined, ReloadOutlined } from "@ant-design/icons";
import ApiService from "../EOLReportApi/ReportApiService";

const { Title } = Typography;
const { Option } = Select;

const GarmentProductionReport = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [operationType, setOperationType] = useState("");
  const [loading, setLoading] = useState(false);
  const [siteTitle, setSiteTitle] = useState("Loading...");
  const [deviceList, setDeviceList] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [operationHeaders, setOperationHeaders] = useState([]);
  const [reportData, setReportData] = useState(null);

  // 🔹 Load site & device info
  useEffect(() => {
    const fetchInitialData = async () => {
      const title = await ApiService.getSiteTitle();
      setSiteTitle(title);
      const devices = await ApiService.getDevices();
      setDeviceList(devices);
    };
    fetchInitialData();
  }, []);

  // 🔹 Load data
  const loadData = async () => {
    if (!fromDate || !toDate) {
      message.warning("Please select From and To Date!");
      return;
    }

    setLoading(true);
    try {
      const res = await ApiService.getOperationsWithMaster(
        fromDate.format("YYYY-MM-DD"),
        toDate.format("YYYY-MM-DD"),
        operationType || null
      );

      setReportData(res);

      const counts = res?.data?.device_statistics?.device_counts;
      if (res?.status && counts) {
        const grouped = {};
        Object.keys(counts).forEach((key) => {
          const item = counts[key];
          const line = item.line_no || "N/A";
          if (!grouped[line]) grouped[line] = [];
          grouped[line].push({
            device_id: key,
            count: item.count,
            op_name: item.op_name || "N/A",
            line_no: line,
          });
        });

        // const table = Object.keys(grouped).map((line) => ({
        //   line_no: line,
        //   ops: grouped[line],
        // }));
        // Filter out rows with null, undefined, or "N/A" line numbers
const table = Object.keys(grouped)
  .filter((line) => line && line !== "N/A" && line.trim() !== "")
  .map((line) => ({
    line_no: line,
    ops: grouped[line],
  }));


        const maxOps = Math.max(...table.map((row) => row.ops.length));
        setOperationHeaders(Array.from({ length: maxOps }, (_, i) => i + 1));
        setTableData(table);
      } else {
        setTableData([]);
        message.info("No data found for selected filters.");
      }
    } catch (err) {
      console.error(err);
      message.error("Error loading data.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Reset
  const resetData = () => {
    setFromDate(null);
    setToDate(null);
    setOperationType("");
    setTableData([]);
  };

  // 🔹 Header text
  const formatDateRange = () => {
    if (!fromDate || !toDate) return "";
    const from = fromDate.format("YYYY-MM-DD");
    const to = toDate.format("YYYY-MM-DD");
    return from === to
      ? `[ ${from} ] No.of Garments Produced`
      : `[ ${from} ] - [ ${to} ] No.of Garments Produced`;
  };

  // ✅ Show Output Pcs only if YUS002 belongs to that line
  const getOutputPcsByLine = (lineNo) => {
    const counts = reportData?.data?.device_statistics?.device_counts;
    if (!counts) return 0;

    const yusData = counts["YUS002"];
    if (yusData && yusData.line_no === lineNo) {
      return yusData.count || 0;
    }

    return 0; // show 0 for all other lines
  };

  return (
    <div style={{ background: "#f6f8fa", minHeight: "100vh", padding: "20px" }}>
      {/* 🔹 Filters */}
      <div
        style={{
          background: "#fff",
          borderRadius: 6,
          padding: 20,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Row gutter={[16, 16]} align="middle" justify="center">
          <Col xs={24} sm={12} md={6}>
            <label style={labelStyle}>From Date</label>
            <DatePicker
              style={inputStyle}
              value={fromDate}
              onChange={setFromDate}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label style={labelStyle}>To Date</label>
            <DatePicker
              style={inputStyle}
              value={toDate}
              onChange={setToDate}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label style={labelStyle}>Device Type</label>
            <Select
              style={inputStyle}
              value={operationType}
              onChange={setOperationType}
              placeholder="Select Operation"
              allowClear
            >
              {deviceList.map((device, idx) => (
                <Option key={idx} value={device}>
                  {device}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space style={{ marginTop: 26 }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={loadData}
                loading={loading}
                style={{ background: "#2150FDFF", border: "none" }}
              >
                Load
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={resetData}
                style={{
                  background: "#2150FDFF",
                  border: "none",
                  color: "#fff",
                }}
              >
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 🔹 Title */}
      <div style={{ textAlign: "center", marginTop: 40 }}>
        <Title level={4} style={{ marginBottom: 0, color: "#333" }}>
          {siteTitle}
        </Title>
        <div
          style={{
            display: "inline-block",
            background: "#eef0f2",
            borderRadius: "30px",
            padding: "8px 20px",
            marginTop: 10,
          }}
        >
          <strong>{formatDateRange()}</strong>
        </div>
      </div>

      {/* 🔹 Table */}
      <div
        style={{
          background: "#fff",
          marginTop: 30,
          borderRadius: 6,
          padding: 20,
          boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
          overflowX: "auto",
        }}
      >
        <Spin spinning={loading}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={thStyle}>Line No</th>
                {operationHeaders.map((num) => (
                  <th key={num} style={thStyle}>
                    Critical Operation {num}
                  </th>
                ))}
                <th style={thStyle}>Output Pcs</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                tableData.map((row, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{row.line_no}</td>
                    {operationHeaders.map((num, j) => {
                      const op = row.ops[j];
                      return (
                        <td key={j} style={tdStyle}>
                          {op ? (
                            <>
                              <div style={{ fontWeight: 600 }}>
                                {op.op_name}
                              </div>
                              <Tag color="cyan">{op.device_id}</Tag>
                              <div>{op.count}</div>
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    })}
                    <td
                      style={{
                        ...tdStyle,
                        fontWeight: 700,
                        color: "#2150FDFF",
                      }}
                    >
                      {getOutputPcsByLine(row.line_no)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={operationHeaders.length + 2} style={tdStyle}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Spin>
      </div>
    </div>
  );
};

// 🔹 Styles
const labelStyle = { display: "block", fontWeight: 600, marginBottom: 5 };
const inputStyle = { width: "100%" };
const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  textAlign: "center",
};
const thStyle = {
  padding: "12px",
  borderBottom: "2px solid #ddd",
  fontWeight: "600",
};
const tdStyle = {
  padding: "10px",
  color: "#333",
  borderBottom: "1px solid #eee",
};

export default GarmentProductionReport;
